import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TicketSort, TicketSortDir } from '../../api/types';
import { DEFAULT_SORT, NATURAL_DIR, parseDir, parseSort } from './list-params';

export interface ListParams {
  sort: TicketSort;
  /** `null` means "the ordering's own direction" — the API is sent no `dir` at all. */
  dir: TicketSortDir | null;
  /** The direction actually in effect, for a header that has to draw an arrow. */
  effectiveDir: TicketSortDir;
  setSort: (sort: TicketSort) => void;
  /** Click a column: select it, or flip it if it is already the active one. */
  toggleSort: (sort: TicketSort) => void;
}

/**
 * The ordering, in the URL, next to the filters and the page.
 *
 * ⭐ `sort=updated` is dropped from the URL when it is the default, so a plain `/list` link is the
 * default view rather than a snapshot of one — the same rule the collapse state follows. `dir` is
 * dropped whenever it equals the ordering's natural direction, for the same reason.
 *
 * 🔴 CHANGING THE ORDER RESETS TO PAGE 1. "Rows 51–100 of the OTHER ordering" is a set of rows that
 * has no meaning; keeping the page number would silently show a slice nobody asked for. The same
 * reasoning `setFilters` already uses for filter changes — and it applies to a DIRECTION flip just as
 * much as to a column change, because reversing the order changes which rows page 2 holds.
 */
export function useListParams(): ListParams {
  const [params, setParams] = useSearchParams();
  const sort = parseSort(params.get('sort'));
  const dir = parseDir(params.get('dir'));

  // ⚠️ What the server will actually do: the explicit direction if there is one, otherwise this
  //    ordering's own. Mirrors the API, whose `updated` is DESC and whose `rank` is ASC.
  const effectiveDir: TicketSortDir = dir ?? NATURAL_DIR[sort];

  const write = useCallback(
    (nextSort: TicketSort, nextDir: TicketSortDir | null) => {
      const updated = new URLSearchParams(params);

      if (nextSort === DEFAULT_SORT) updated.delete('sort');
      else updated.set('sort', nextSort);

      // ⭐ Only when it differs from what the ordering would do anyway — `?sort=rank&dir=asc` and
      //   `?sort=rank` are the same view, and only one of them should be the link people copy.
      if (nextDir === null || nextDir === NATURAL_DIR[nextSort]) updated.delete('dir');
      else updated.set('dir', nextDir);

      updated.delete('page');
      // A change of VIEW, not of place — Back should leave the list, not undo a sort.
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // Selecting an ordering from the dropdown takes its natural direction, never the previous
  // column's. ⛔ Carrying `dir` across a column change is how "priority, ascending" silently becomes
  // "points, ascending" — a different question than the one that was clicked.
  const setSort = useCallback((next: TicketSort) => write(next, null), [write]);

  const toggleSort = useCallback(
    (next: TicketSort) => {
      // First click selects the column in its natural direction; clicking the ACTIVE column flips it.
      if (next !== sort) {
        write(next, null);
        return;
      }
      write(next, effectiveDir === 'asc' ? 'desc' : 'asc');
    },
    [sort, effectiveDir, write],
  );

  return { sort, dir, effectiveDir, setSort, toggleSort };
}
